import * as React from "react";
import { useState, useEffect } from "react";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import Box from "@mui/material/Box";
import Toolbar from "@mui/material/Toolbar";
import Grid from "@mui/material/Grid";
import CardsDashboard from "../../components/dashboard/overview/cardsDashboard";
import {
  AssignmentLate,
  CarCrash,
  Checklist,
  Close,
  Group,
} from "@mui/icons-material";
import BarChartCardDashboard from "../../components/dashboard/overview/barChartCardDashboard";
import { getDashboardData, getEmpresasData } from "../../api/dashboardApi";
import { generateColors } from "../../services/generateColors";
import {
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Typography,
  Modal,
  TableContainer,
  Button,
  Pagination,
  List,
  ListItem,
  ListItemText,
  Divider,
  IconButton,
} from "@mui/material";
import { DashboardData } from "../../types/dashboard";
import {
  getChecklistAtrasadoExcel,
  getVehiclesSemChecklist,
  getVehiclesWithChecklistAtrasado,
} from "../../api/vehicleApi";
import { formatDate, formatDateTime } from "../../services/formatDate";
import { useNavigate } from "react-router-dom";
import * as XLSX from "xlsx";
import { toast } from "react-toastify";
import { VehicleData } from "../../types/vehicleData";

const defaultTheme = createTheme({
  typography: {
    fontFamily: "Poppins, sans-serif",
  },
});

interface Acao {
  checklist: string;
  data_criacao?: string | null;
}

export default function Dashboard() {
  const token = localStorage.getItem("token");
  const navigate = useNavigate();
  const [dashboardData, setDashboardData] = useState<DashboardData>();
  const [empresas, setEmpresas] = useState<any[]>([]);
  const [selectedEmpresa, setSelectedEmpresa] = useState<number | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [checklistAtrasados, setChecklistAtrasados] = useState<any[]>([]);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [totalPages, setTotalPages] = useState<number>(1);
  const [isVeiculosSemChecklistModal, setIsVeiculosSemChecklistModal] =
    useState<boolean>(false);
  const [isLoadingModal, setIsLoadingModal] = useState<boolean>(false);
  const [isExportingExcel, setIsExportingExcel] = useState(false);

  const handleChangePage = async (
    event: React.ChangeEvent<unknown>,
    page: number
  ) => {
    setCurrentPage(page);
    if (isVeiculosSemChecklistModal) {
      await fetchVeiculosSemChecklist(page);
    } else {
      await fetchChecklistAtrasados(page);
    }
  };

  const fetchChecklistAtrasados = async (page: number) => {
    try {
      if (!token) {
        throw new Error("Token de autenticação não encontrado");
      }
      setIsLoadingModal(true);
      const response = await getVehiclesWithChecklistAtrasado(
        token,
        selectedEmpresa,
        page
      );
      if (Array.isArray(response.data)) {
        setChecklistAtrasados(response.data);
        setCurrentPage(response.current_page);
        setTotalPages(response.last_page);
      } else {
        console.error("Os dados recebidos não são um array:", response.data);
      }
    } catch (error) {
      console.error("Erro ao buscar veículos com checklist atrasado:", error);
    } finally {
      setIsLoadingModal(false);
    }
  };

  const fetchVeiculosSemChecklist = async (page: number) => {
    try {
      if (!token) {
        throw new Error("Token de autenticação não encontrado");
      }
      setIsLoadingModal(true);
      const response = await getVehiclesSemChecklist(
        token,
        selectedEmpresa,
        page
      );
      setChecklistAtrasados(response.data);
      setCurrentPage(response.current_page);
      setTotalPages(response.last_page);
    } catch (error) {
      console.error("Erro ao buscar veículos sem checklist:", error);
    } finally {
      setIsLoadingModal(false);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(true);
        if (!token) {
          console.error("Token não encontrado.");
          return;
        }

        const empresasData = await getEmpresasData(token);
        setEmpresas(empresasData);

        if (empresasData.length === 1) {
          setSelectedEmpresa(empresasData[0].id);
        }
      } catch (error) {
        console.error("Erro ao buscar dados das empresas:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [token]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(true);
        if (!token) {
          console.error("Token não encontrado.");
          return;
        }
        const data = await getDashboardData(token, selectedEmpresa);
        setDashboardData(data);
      } catch (error) {
        console.error("Erro ao buscar dados do dashboard:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [token, selectedEmpresa]);

  const handleEmpresaChange = (event: SelectChangeEvent<number>) => {
    setSelectedEmpresa(
      typeof event.target.value === "string"
        ? parseInt(event.target.value)
        : event.target.value
    );
  };

  const handleChecklistAtrasadosClick = async (page: number = 1) => {
    try {
      if (!token) {
        throw new Error("Token de autenticação não encontrado");
      }
      setIsLoadingModal(true);
      setIsModalOpen(true);
      await fetchChecklistAtrasados(page);
      setIsVeiculosSemChecklistModal(false);
    } catch (error) {
      console.error("Erro ao buscar veículos com checklist atrasado:", error);
    } finally {
      setIsLoadingModal(false);
    }
  };

  const handleVeiculosSemChecklistClick = async (page: number = 1) => {
    try {
      if (!token) {
        throw new Error("Token de autenticação não encontrado");
      }
      setIsLoadingModal(true);
      setIsModalOpen(true);
      await fetchVeiculosSemChecklist(page);
      setIsVeiculosSemChecklistModal(true);
    } catch (error) {
      console.error("Erro ao buscar veículos sem checklist:", error);
    } finally {
      setIsLoadingModal(false);
    }
  };

  const handleExportExcel = async () => {
    try {
      if (!token) throw new Error("Token de autenticação não encontrado");

      setIsExportingExcel(true);

      const allData: VehicleData[] = await getChecklistAtrasadoExcel(
        token,
        selectedEmpresa
      );

      if (!allData || allData.length === 0) {
        toast.error("Não há dados para exportar.");
        return;
      }

      const empresaSelecionada = empresas.find((e) => e.id === selectedEmpresa);
      const empresaNome = empresaSelecionada
        ? empresaSelecionada.nome_empresa
        : "Todas";

      const allChecklistNames: string[] = Array.from(
        new Set(
          allData.flatMap((row: VehicleData) =>
            row.acoes.map((a: Acao) => a.checklist.toUpperCase())
          )
        )
      );

      const exportData = allData.map((row: VehicleData) => {
        const obj: Record<string, string> = {
          "Chassi do Veículo": row.chassi_veiculo,
          Modelo: row.descricao_modelo,
          Cor: row.descricao_cor,
          "Data Armazenagem": formatDate(row.data_armazenagem_veiculo),
        };

        allChecklistNames.forEach((name: string) => {
          const acao = row.acoes.find(
            (a: Acao) => a.checklist.toUpperCase() === name
          );
          obj[name] = acao
            ? acao.data_criacao
              ? formatDateTime(acao.data_criacao)
              : "Não preenchido"
            : "NÃO POSSUI ESSE CHECKLIST";
        });

        return obj;
      });

      const worksheet = XLSX.utils.json_to_sheet(exportData || []);

      const range = XLSX.utils.decode_range(worksheet["!ref"]!);
      for (let C = range.s.c; C <= range.e.c; ++C) {
        const cell_address = XLSX.utils.encode_cell({ r: 0, c: C });
        if (!worksheet[cell_address]) continue;
        worksheet[cell_address].s = {
          font: { bold: true, color: { rgb: "FFFFFFFF" } },
          fill: { fgColor: { rgb: "FF0000FF" } },
          alignment: { horizontal: "center" },
        };
      }

      const colWidths: { wch: number }[] = [
        { wch: 20 },
        { wch: 20 },
        { wch: 15 },
        { wch: 20 },
      ];

      const numAcoes = allChecklistNames.length;
      for (let i = 0; i < numAcoes; i++) {
        colWidths.push({ wch: 55 });
      }

      worksheet["!cols"] = colWidths;

      for (let R = 1; R <= range.e.r; ++R) {
        for (let C = 0; C <= range.e.c; ++C) {
          const cell_address = XLSX.utils.encode_cell({ r: R, c: C });
          const cell = worksheet[cell_address];
          if (cell && cell.v === "NÃO POSSUI ESSE CHECKLIST") {
            cell.s = {
              font: { color: { rgb: "FFFF0000" }, bold: true },
            };
          }
        }
      }

      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Checklist Atrasados");

      XLSX.writeFile(workbook, `Checklist_Atrasados_${empresaNome}.xlsx`, {
        compression: true,
      });
    } catch (error) {
      console.error("Erro ao exportar Excel:", error);
      toast.error("Erro ao exportar dados. Tente novamente.");
    } finally {
      setIsExportingExcel(false);
    }
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setCurrentPage(1);
  };

  if (isLoading || !dashboardData) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <CircularProgress sx={{ color: "#000" }} />
        <Typography variant="h5" sx={{ mt: 2 }}>
          Carregando Dashboard
        </Typography>
      </Box>
    );
  }

  const userLabels = dashboardData.qnt_usuario.model;
  const vehicleLabels = dashboardData.qnt_modelo.model;
  const localLabels = dashboardData.qnt_local.model;

  const numColors = userLabels.length;
  const numColorsV = vehicleLabels.length;
  const numColorsL = localLabels.length;

  const colors = generateColors(numColors);
  const colorsV = generateColors(numColorsV);
  const colorsL = generateColors(numColorsL);

  const cardsData = [
    {
      title: "Checklist Realizados",
      value: dashboardData.cab.qnt_checklist,
      color: "green",
      icon: <Checklist sx={{ fontSize: 30 }} />,
      grid: { lg: 3, sm: 6, xs: 12 },
    },
    {
      title: "Checklist Atrasados",
      value: dashboardData.cab.checklist_atraso,
      color: "red",
      icon: <AssignmentLate sx={{ fontSize: 30 }} />,
      grid: { lg: 3, sm: 6, xs: 12 },
      onClick: handleChecklistAtrasadosClick,
    },
    {
      title: "Veículos sem checklist",
      value: dashboardData.cab.veiculos_s_checklist,
      color: "#fd7e14",
      icon: <CarCrash sx={{ fontSize: 30 }} />,
      grid: { lg: 3, sm: 6, xs: 12 },
      onClick: handleVeiculosSemChecklistClick,
    },
    {
      title: "Veículos sem cliente",
      value: dashboardData.cab.veiculos_s_clientes,
      color: "#0aa2c0",
      icon: <Group sx={{ fontSize: 30 }} />,
      grid: { lg: 3, sm: 6, xs: 12 },
    },
  ];

  const chartData = [
    {
      title: "Veículos por local",
      xAxisData:
        dashboardData.qnt_local.model.length > 0
          ? dashboardData.qnt_local.model
          : [],
      seriesData:
        dashboardData.qnt_local.data.length > 0
          ? [{ data: dashboardData.qnt_local.data }]
          : [],
      colors: colorsL,
      height: 280,
      grid: { lg: 6, md: 6, xs: 12 },
      subtitle: "Tempo Médio de Armazenagem",
      subtitleY: `${Math.floor(dashboardData.cab.tempo_armazenagem)} Dia(s)`,
      subtitle2: "Total Locais",
      subtitleX: `${dashboardData.cab.total_locais}`,
    },
    {
      title: "Veículos por modelo",
      xAxisData:
        dashboardData.qnt_modelo.model.length > 0
          ? dashboardData.qnt_modelo.model
          : [],
      seriesData:
        dashboardData.qnt_modelo.data.length > 0
          ? [{ data: dashboardData.qnt_modelo.data }]
          : [],
      colors: colorsV,
      height: 280,
      grid: { lg: 6, md: 6, xs: 12 },
      subtitle: "Total Modelos",
      subtitleY: `${dashboardData.cab.total_modelos}`,
      subtitle2: "Total Veículos",
      subtitleX: `${dashboardData.cab.total_veiculos}`,
    },
    {
      title: "Checklist por usuário",
      xAxisData:
        dashboardData.qnt_usuario.model.length > 0
          ? dashboardData.qnt_usuario.model
          : [],
      seriesData:
        dashboardData.qnt_usuario.data.length > 0
          ? [{ data: dashboardData.qnt_usuario.data }]
          : [],
      colors: colors,
      height: 280,
      grid: { lg: 12, md: 12, xs: 12 },
      subtitle: "Checklists Realizados",
      subtitleY: `${dashboardData.cab.qnt_checklist}`,
      subtitle2: "Total Clientes",
      subtitleX: `${dashboardData.cab.total_clientes}`,
    },
  ];

  return (
    <ThemeProvider theme={defaultTheme}>
      <Box sx={{ display: "flex" }} p={2} mt={-4}>
        <Box
          sx={{
            flexGrow: 1,
            overflow: "auto",
          }}
        >
          <Toolbar />

          <Grid container spacing={0}>
            <FormControl
              fullWidth
              sx={{ mb: { xs: 3, sm: 3, lg: 1 }, padding: 1 }}
            >
              {selectedEmpresa === null || selectedEmpresa === undefined ? (
                <InputLabel id="demo-simple-select-label" sx={{ mt: 1, ml: 1 }}>
                  Todas as Empresas
                </InputLabel>
              ) : null}
              <Select
                labelId="demo-simple-select-label"
                id="demo-simple-select"
                value={selectedEmpresa != null ? selectedEmpresa : ""}
                onChange={handleEmpresaChange}
                label={
                  selectedEmpresa === null || selectedEmpresa === undefined
                    ? "Todas as Empresas"
                    : null
                }
                sx={{
                  "& .MuiSelect-root": {
                    borderRadius: 3,
                    "&:focus": {
                      backgroundColor: "#ffddec",
                      borderColor: "red",
                    },
                  },
                  ".MuiOutlinedInput-notchedOutline": {
                    borderRadius: 3,
                    boxShadow: 2,
                  },
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    color: "#303030",
                    borderWidth: "2px",
                  },
                }}
                renderValue={(selected) => {
                  return (
                    <span style={{ color: "black" }}>
                      {Number.isNaN(selected)
                        ? "Todas as Empresas"
                        : empresas.find((emp) => emp.id === selected)
                            ?.nome_empresa || NaN}
                    </span>
                  );
                }}
              >
                <MenuItem value="">Todas as Empresas</MenuItem>
                {empresas.map((empresa) => (
                  <MenuItem key={empresa.id} value={empresa.id}>
                    {empresa.nome_empresa}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {cardsData.map((card, index) => (
              <Grid
                item={true}
                key={index}
                {...card.grid}
                sx={{ marginTop: { xs: -3, sm: -3, lg: 0 } }}
              >
                {card.title === "Veículos sem checklist" ||
                card.title === "Checklist Atrasados" ? (
                  <div onClick={() => card.onClick && card.onClick()}>
                    <CardsDashboard
                      title={card.title}
                      value={card.value}
                      color={card.color}
                      icon={card.icon}
                    />
                  </div>
                ) : (
                  <CardsDashboard
                    title={card.title}
                    value={card.value}
                    color={card.color}
                    icon={card.icon}
                  />
                )}
              </Grid>
            ))}
            {chartData.map((chart, index) => (
              <Grid
                item={true}
                key={index}
                {...chart.grid}
                sx={{ marginTop: { xs: -5, sm: -5, lg: -2 } }}
              >
                <BarChartCardDashboard
                  title={chart.title}
                  xAxisData={chart.xAxisData}
                  seriesData={chart.seriesData}
                  colors={chart.colors}
                  height={chart.height}
                  subtitle={chart.subtitle}
                  subtitleY={chart.subtitleY}
                  subtitle2={chart.subtitle2}
                  subtitleX={chart.subtitleX}
                />
              </Grid>
            ))}
          </Grid>
          <Modal
            aria-labelledby="transition-modal-title"
            aria-describedby="transition-modal-description"
            open={isModalOpen}
            onClose={handleCloseModal}
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {isLoadingModal ? (
              <Box
                sx={{
                  backgroundColor: "#fff",
                  borderRadius: 2,
                  boxShadow: 24,
                  p: 2,
                  width: "90%",
                  height: "auto",
                  maxWidth: "100%",
                  textAlign: "center",
                }}
              >
                <Typography id="modal-modal-title" variant="h6" component="h2">
                  Carregando dados...
                </Typography>
                <CircularProgress sx={{ color: "#000", mt: 2 }} />
              </Box>
            ) : (
              <>
                <Box
                  sx={{
                    backgroundColor: "#fff",
                    borderRadius: 2,
                    boxShadow: 24,
                    p: 2,
                    width: { xs: "80%", sm: "100%" },
                    height: { xs: "70%", sm: "800px" },
                    maxWidth: "700px",
                    textAlign: "center",
                    overflow: "auto",
                  }}
                >
                  <Box
                    sx={{
                      position: "relative",
                      textAlign: "center",
                    }}
                  >
                    <Typography
                      id="modal-modal-title"
                      variant="h6"
                      component="h2"
                      textAlign="center"
                    >
                      {isVeiculosSemChecklistModal
                        ? "Veículos Sem Checklist"
                        : "Checklist Atrasados"}
                    </Typography>

                    <IconButton
                      onClick={handleCloseModal}
                      sx={{
                        position: "absolute",
                        top: 0,
                        right: 0,
                        backgroundColor: "red",
                        color: "white",
                        ":hover": {
                          bgcolor: "#5c5c5c",
                        },
                      }}
                    >
                      <Close />
                    </IconButton>
                  </Box>

                  <TableContainer>
                    <List>
                      {checklistAtrasados.map((veiculo: any, index: number) => (
                        <>
                          <ListItem key={index} disablePadding>
                            <ListItemText
                              primary={`Chassi do Veículo: ${veiculo.chassi_veiculo}`}
                              secondary={
                                <>
                                  <Typography
                                    component="span"
                                    variant="body2"
                                    color="textPrimary"
                                    mr={3}
                                    mt={1}
                                  >
                                    Modelo: {veiculo.descricao_modelo}{" "}
                                  </Typography>
                                  <Typography
                                    component="span"
                                    variant="body2"
                                    color="textPrimary"
                                    mr={3}
                                  >
                                    Cor: {veiculo.descricao_cor}{" "}
                                  </Typography>
                                  <Typography
                                    component="span"
                                    variant="body2"
                                    color="textPrimary"
                                  >
                                    Data Armazenagem:{" "}
                                    {formatDate(
                                      veiculo.data_armazenagem_veiculo
                                    )}
                                    <br />
                                  </Typography>
                                  {veiculo.acoes.map(
                                    (acao: any, index: number) => (
                                      <Typography
                                        key={index}
                                        component="span"
                                        variant="body2"
                                        color={
                                          acao.data_criacao
                                            ? "inherit"
                                            : "error"
                                        }
                                        marginLeft={2}
                                      >
                                        <b
                                          style={{
                                            color: "#303030",
                                            marginRight: 7,
                                          }}
                                        >
                                          - {acao.checklist.toUpperCase()}:
                                        </b>

                                        {acao.data_criacao
                                          ? `${formatDateTime(
                                              acao.data_criacao
                                            )}`
                                          : "Não preenchido"}
                                        <br />
                                      </Typography>
                                    )
                                  )}
                                </>
                              }
                            />
                          </ListItem>
                          <Divider></Divider>
                        </>
                      ))}
                    </List>
                  </TableContainer>

                  <Pagination
                    count={totalPages}
                    page={currentPage}
                    onChange={handleChangePage}
                    sx={{ mt: 2, textAlign: "center" }}
                  />
                  {!isVeiculosSemChecklistModal && (
                    <Button
                      variant="contained"
                      color="success"
                      fullWidth
                      onClick={handleExportExcel}
                      sx={{
                        mt: 2,
                        backgroundColor: "green",
                        ":hover": { bgcolor: "darkgreen" },
                      }}
                      disabled={
                        isExportingExcel || checklistAtrasados.length === 0
                      }
                    >
                      {isExportingExcel ? (
                        <>
                          <CircularProgress
                            size={20}
                            sx={{ color: "white", mr: 1 }}
                          />
                          Gerando Excel...
                        </>
                      ) : (
                        "Exportar para Excel"
                      )}
                    </Button>
                  )}

                  <Button
                    variant="contained"
                    color="primary"
                    fullWidth
                    onClick={() => {
                      const params = new URLSearchParams();
                      if (isVeiculosSemChecklistModal) {
                        params.append("filter", "sem_checklist");
                      } else {
                        params.append("filter", "atrasados");
                      }
                      if (selectedEmpresa) {
                        params.append("id_empresa", selectedEmpresa.toString());
                      }
                      navigate(`/veiculos?${params.toString()}`);
                    }}
                    sx={{
                      backgroundColor: "#303030",
                      ":hover": {
                        bgcolor: "#5c5c5c",
                      },
                      mt: 2,
                    }}
                  >
                    Visualizar Veículos
                  </Button>
                </Box>
              </>
            )}
          </Modal>
        </Box>
      </Box>
    </ThemeProvider>
  );
}
